import React, { useEffect, useState } from 'react';
import { fetchWidgetBundles, parseWidgetManifest, type WidgetBundleInfo } from '../../services/myToolsApi';
import WidgetPermissionList from './WidgetPermissionList';
import '../../styles/Widgets.css';

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

const COLLAPSED_REVISION_COUNT = 3;

/** Revision history of a widget's uploaded bundles (my_tool_widget_bundles), newest first.
 *  Shows what changed between versions at the permission level — the thing installers care about.
 *  A widget iterated on a lot (dev testing, frequent updates) can accumulate a long history; only
 *  the most recent few show by default so this section doesn't dominate the review page. */
const WidgetVersionHistory: React.FC<{ toolId: string; currentRevision?: number }> = ({ toolId, currentRevision }) => {
  const [bundles, setBundles] = useState<WidgetBundleInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setBundles(null);
    setError(null);
    setShowAll(false);
    fetchWidgetBundles(toolId, controller.signal)
      .then(list => { if (!controller.signal.aborted) setBundles([...list].sort((a, b) => b.revision - a.revision)); })
      .catch(err => { if (!controller.signal.aborted) setError(err instanceof Error ? err.message : 'Failed to load version history'); });
    return () => controller.abort();
  }, [toolId]);

  if (error) return <p className="wg-history__empty">{error}</p>;
  if (bundles === null) return <p className="wg-history__empty">Loading version history…</p>;
  if (bundles.length === 0) return <p className="wg-history__empty">No bundle has been uploaded for this widget yet.</p>;

  const visible = showAll ? bundles : bundles.slice(0, COLLAPSED_REVISION_COUNT);
  const hiddenCount = bundles.length - visible.length;

  return (
    <>
    <ol className="wg-history">
      {visible.map((b, index) => {
        const manifest = parseWidgetManifest(b.manifest);
        const older = bundles[index + 1] ? parseWidgetManifest(bundles[index + 1].manifest) : null;
        const current = new Set(manifest?.permissions ?? []);
        const previous = new Set(older?.permissions ?? []);
        const added = Array.from(current).filter(p => !previous.has(p));
        const removed = Array.from(previous).filter(p => !current.has(p));
        const isCurrent = currentRevision ? b.revision === currentRevision : index === 0;
        return (
          <li key={b.id ?? b.revision} className={`wg-history__item${isCurrent ? ' wg-history__item--current' : ''}`}>
            <div className="wg-history__head">
              <span className="wg-chip wg-chip--accent">v{b.version || manifest?.version || '?'}</span>
              <span className="wg-history__rev">revision {b.revision}</span>
              {isCurrent && <span className="wg-chip">current</span>}
              <span className="wg-history__date">{formatDate(b.createdAt)}</span>
            </div>
            {manifest?.changelog && <p className="wg-listing__changelog" style={{ marginTop: 6 }}>{manifest.changelog}</p>}
            <div className="wg-history__meta">
              {b.entryFile && <span>entry {b.entryFile}</span>}
              {typeof b.fileCount === 'number' && <span>{b.fileCount} file{b.fileCount === 1 ? '' : 's'}</span>}
              <span>{formatBytes(b.sizeBytes)} uploaded</span>
              {b.flattenedSizeBytes ? <span>{formatBytes(b.flattenedSizeBytes)} flattened</span> : null}
            </div>
            {older && (added.length > 0 || removed.length > 0) ? (
              <div className="wg-history__diff">
                {[...added, ...removed].every(p => p.startsWith('network:')) && (
                  <span className="wg-chip" title="Only network: permissions changed between these two revisions — based on the permission list, not a full code diff. Still worth a quick look at the new origin(s).">
                    🌐 Network-only change
                  </span>
                )}
                {added.map(p => <span key={`+${p}`} className="wg-chip wg-chip--warn">+ {p}</span>)}
                {removed.map(p => <span key={`-${p}`} className="wg-chip">− {p}</span>)}
              </div>
            ) : (
              index === bundles.length - 1 && <WidgetPermissionList manifest={manifest} variant="chips" emptyText="No permissions" />
            )}
          </li>
        );
      })}
    </ol>
    {hiddenCount > 0 && (
      <button type="button" className="wg-history__more" onClick={() => setShowAll(true)}>
        Show {hiddenCount} earlier revision{hiddenCount === 1 ? '' : 's'}
      </button>
    )}
    </>
  );
};

export default WidgetVersionHistory;
